import { useState } from "react";
import type { MarketHistoryPoint } from "../types/index.ts";

interface MarketTrendChartProps {
  data: MarketHistoryPoint[];
  period: number;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getUTCDate()}`;
}

export function MarketTrendChart({ data, period }: MarketTrendChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (data.length < 2) {
    return <div className="td-chart-empty">Not enough history data</div>;
  }

  const scores = data.map((d) => d.avgScore);
  const min = Math.min(...scores) - 2;
  const max = Math.max(...scores) + 2;
  const range = max - min || 1;

  const width = 320;
  const height = 120;
  const pad = 8;

  const coords = scores.map((score, i) => ({
    x: pad + (i / (scores.length - 1)) * (width - pad * 2),
    y: height - pad - ((score - min) / range) * (height - pad * 2),
  }));

  const points = coords.map((c) => `${c.x},${c.y}`).join(" ");

  const hovered = hoverIdx !== null ? coords[hoverIdx] : null;
  const hoveredScore = hoverIdx !== null ? scores[hoverIdx] : null;
  const hoveredDate = hoverIdx !== null ? data[hoverIdx]?.date : null;

  return (
    <div className="td-trend-chart">
      <div className="td-chart-label">
        {hoverIdx !== null
          ? `${formatDate(hoveredDate ?? "")} · Avg ${hoveredScore?.toFixed(1)}`
          : `Market Avg · ${period}d`}
      </div>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <title>Market Average Trend</title>
        <polyline
          fill="none"
          stroke="var(--accent-primary)"
          strokeWidth="1.5"
          opacity="0.4"
          points={points}
        />
        <polyline fill="none" stroke="var(--accent-primary)" strokeWidth="2" points={points} />
        {hovered && (
          <>
            <line
              x1={hovered.x}
              y1={pad}
              x2={hovered.x}
              y2={height - pad}
              stroke="var(--muted)"
              strokeWidth="1"
              strokeDasharray="3,3"
              opacity="0.5"
            />
            <circle cx={hovered.x} cy={hovered.y} r="4" fill="var(--accent-primary)" />
          </>
        )}
        {coords.map((c, i) => (
          // biome-ignore lint/a11y/noStaticElementInteractions: SVG hover region
          <rect
            key={data[i]?.date}
            x={i === 0 ? 0 : (c.x + (coords[i - 1]?.x ?? c.x)) / 2}
            y={0}
            width={
              i === 0
                ? (c.x + (coords[1]?.x ?? c.x)) / 2
                : i === coords.length - 1
                  ? width - (c.x + (coords[i - 1]?.x ?? c.x)) / 2
                  : ((coords[i + 1]?.x ?? c.x) - (coords[i - 1]?.x ?? c.x)) / 2
            }
            height={height}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
          />
        ))}
      </svg>
    </div>
  );
}
